import { createTheme, ThemeOptions } from '@mui/material/styles'

// Taylin Digital Brand Colors
const colors = {
  // Primary brand colors from logo
  blue: '#00B4D8',
  blueLight: '#33C4E2',
  blueDark: '#0077B6',

  green: '#90E0EF',
  greenLight: '#B8F2FF',
  greenDark: '#48CAE4',

  orange: '#F77F00',
  orangeLight: '#FF9F40',
  orangeDark: '#D62D20',

  techBlue: '#2B5CE6',
  techBlueLight: '#5B7CEB',
  techBlueDark: '#1E40AF',

  // Neutral colors
  gray50: '#FAFAFA',
  gray100: '#F5F5F5',
  gray200: '#EEEEEE',
  gray300: '#E0E0E0',
  gray400: '#BDBDBD',
  gray500: '#9E9E9E',
  gray600: '#757575',
  gray700: '#616161',
  gray800: '#424242',
  gray900: '#212121'
}

// Base theme configuration
const baseThemeOptions: ThemeOptions = {
  typography: {
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    h1: {
      fontWeight: 700,
      fontSize: '2.25rem',
      lineHeight: 1.25
    },
    h2: {
      fontWeight: 600,
      fontSize: '1.875rem',
      lineHeight: 1.25
    },
    h3: {
      fontWeight: 600,
      fontSize: '1.5rem',
      lineHeight: 1.25
    },
    h4: {
      fontWeight: 600,
      fontSize: '1.25rem',
      lineHeight: 1.25
    },
    h5: {
      fontWeight: 600,
      fontSize: '1.125rem',
      lineHeight: 1.25
    },
    h6: {
      fontWeight: 600,
      fontSize: '1rem',
      lineHeight: 1.25
    },
    body1: {
      fontSize: '1rem',
      lineHeight: 1.5
    },
    body2: {
      fontSize: '0.875rem',
      lineHeight: 1.5
    },
    button: {
      fontWeight: 600,
      textTransform: 'none',
      fontSize: '0.875rem'
    },
    caption: {
      fontSize: '0.75rem',
      lineHeight: 1.5
    }
  },
  shape: {
    borderRadius: 12
  },
  spacing: 8,
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          padding: '10px 24px',
          fontSize: '0.875rem',
          fontWeight: 600,
          transition: 'all 0.2s ease',
          '&:hover': {
            transform: 'translateY(-1px)',
            boxShadow: '0 4px 8px rgba(0, 0, 0, 0.15)'
          }
        },
        sizeSmall: {
          padding: '6px 16px',
          fontSize: '0.8125rem'
        },
        sizeLarge: {
          padding: '12px 32px',
          fontSize: '0.9375rem'
        }
      }
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
          border: `1px solid ${colors.gray200}`,
          transition: 'all 0.2s ease',
          '&:hover': {
            boxShadow: '0 8px 16px rgba(0, 0, 0, 0.1)',
            transform: 'translateY(-2px)'
          }
        }
      }
    },
    MuiCardHeader: {
      styleOverrides: {
        root: {
          padding: '16px 24px',
          '& .MuiCardHeader-title': {
            fontSize: '1.125rem',
            fontWeight: 600
          }
        }
      }
    },
    MuiCardContent: {
      styleOverrides: {
        root: {
          padding: '16px 24px',
          '&:last-child': {
            paddingBottom: '24px'
          }
        }
      }
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          fontWeight: 500,
          fontSize: '0.8125rem'
        },
        colorPrimary: {
          backgroundColor: colors.blue,
          color: 'white',
          '&:hover': {
            backgroundColor: colors.blueDark
          }
        },
        colorSecondary: {
          backgroundColor: colors.techBlue,
          color: 'white',
          '&:hover': {
            backgroundColor: colors.techBlueDark
          }
        }
      }
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          border: 'none',
          '& .MuiAlert-icon': {
            fontSize: '1.25rem'
          }
        },
        standardSuccess: {
          backgroundColor: 'rgba(72, 202, 228, 0.1)',
          color: colors.greenDark,
          '& .MuiAlert-icon': {
            color: colors.greenDark
          }
        },
        standardWarning: {
          backgroundColor: 'rgba(247, 127, 0, 0.1)',
          color: colors.orangeDark,
          '& .MuiAlert-icon': {
            color: colors.orange
          }
        },
        standardError: {
          backgroundColor: 'rgba(211, 47, 47, 0.1)',
          color: '#C62828'
        },
        standardInfo: {
          backgroundColor: 'rgba(0, 180, 216, 0.1)',
          color: colors.blueDark,
          '& .MuiAlert-icon': {
            color: colors.blue
          }
        }
      }
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 8,
            '& fieldset': {
              borderColor: colors.gray300
            },
            '&:hover fieldset': {
              borderColor: colors.gray400
            },
            '&.Mui-focused fieldset': {
              borderColor: colors.blue,
              borderWidth: 2
            }
          }
        }
      }
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
          borderBottom: `1px solid ${colors.gray200}`
        }
      }
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          borderRight: `1px solid ${colors.gray200}`,
          boxShadow: 'none'
        }
      }
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          margin: '2px 8px',
          '&.Mui-selected': {
            backgroundColor: 'rgba(0, 180, 216, 0.1)',
            '&:hover': {
              backgroundColor: 'rgba(0, 180, 216, 0.15)'
            }
          }
        }
      }
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none'
        },
        elevation1: {
          boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)'
        },
        elevation2: {
          boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)'
        },
        elevation3: {
          boxShadow: '0 8px 16px rgba(0, 0, 0, 0.15)'
        }
      }
    }
  }
}

// Customer Portal Theme (Welcoming Blue-Green)
export const customerTheme = createTheme({
  ...baseThemeOptions,
  palette: {
    mode: 'light',
    primary: {
      main: colors.blue,
      light: colors.blueLight,
      dark: colors.blueDark,
      contrastText: '#FFFFFF'
    },
    secondary: {
      main: colors.green,
      light: colors.greenLight,
      dark: colors.greenDark,
      contrastText: '#FFFFFF'
    },
    success: {
      main: colors.greenDark,
      light: colors.green,
      dark: colors.blueDark
    },
    warning: {
      main: colors.orange,
      light: colors.orangeLight,
      dark: colors.orangeDark
    },
    error: {
      main: '#D32F2F',
      light: '#EF5350',
      dark: '#C62828'
    },
    info: {
      main: colors.blue,
      light: colors.blueLight,
      dark: colors.blueDark
    },
    background: {
      default: colors.gray50,
      paper: '#FFFFFF'
    },
    text: {
      primary: colors.gray900,
      secondary: colors.gray600,
      disabled: colors.gray400
    },
    divider: colors.gray200
  }
})

// Agent Portal Theme (Professional Tech Blue)
export const agentTheme = createTheme({
  ...baseThemeOptions,
  palette: {
    mode: 'light',
    primary: {
      main: colors.techBlue,
      light: colors.techBlueLight,
      dark: colors.techBlueDark,
      contrastText: '#FFFFFF'
    },
    secondary: {
      main: colors.blueDark,
      light: colors.blue,
      dark: '#003d66',
      contrastText: '#FFFFFF'
    },
    success: {
      main: colors.greenDark,
      light: colors.green,
      dark: colors.blueDark
    },
    warning: {
      main: colors.orange,
      light: colors.orangeLight,
      dark: colors.orangeDark
    },
    error: {
      main: '#D32F2F',
      light: '#EF5350',
      dark: '#C62828'
    },
    info: {
      main: colors.techBlue,
      light: colors.techBlueLight,
      dark: colors.techBlueDark
    },
    background: {
      default: colors.gray50,
      paper: '#FFFFFF'
    },
    text: {
      primary: colors.gray900,
      secondary: colors.gray600,
      disabled: colors.gray400
    },
    divider: colors.gray200
  }
})

// Admin Portal Theme (Authoritative Dark Blue)
export const adminTheme = createTheme({
  ...baseThemeOptions,
  palette: {
    mode: 'light',
    primary: {
      main: colors.techBlueDark,
      light: colors.techBlue,
      dark: '#1E3A8A',
      contrastText: '#FFFFFF'
    },
    secondary: {
      main: colors.gray700,
      light: colors.gray600,
      dark: colors.gray800,
      contrastText: '#FFFFFF'
    },
    success: {
      main: colors.greenDark,
      light: colors.green,
      dark: colors.blueDark
    },
    warning: {
      main: colors.orangeDark,
      light: colors.orange,
      dark: '#B71C1C'
    },
    error: {
      main: '#D32F2F',
      light: '#EF5350',
      dark: '#C62828'
    },
    info: {
      main: colors.techBlueDark,
      light: colors.techBlue,
      dark: '#1E3A8A'
    },
    background: {
      default: colors.gray50,
      paper: '#FFFFFF'
    },
    text: {
      primary: colors.gray900,
      secondary: colors.gray600,
      disabled: colors.gray400
    },
    divider: colors.gray200
  }
})

// Theme selector function
export const getThemeForRole = (role: string) => {
  switch (role.toLowerCase()) {
    case 'customer':
      return customerTheme
    case 'oversight':
      return customerTheme // Oversight users use customer theme with enhanced visibility
    case 'agent':
      return agentTheme
    case 'admin':
      return adminTheme
    default:
      return customerTheme
  }
}

// Export colors for use in components
export { colors }

// Export individual themes
export const themes = {
  customer: customerTheme,
  agent: agentTheme,
  admin: adminTheme
}

// Default theme (customer theme for backward compatibility)
export const theme = customerTheme